import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { LookupService, MaterialSearchResult } from '../../services/lookup.service';

@Component({
  selector: 'app-material-autosuggest',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './material-autosuggest.component.html',
  styleUrls: ['./material-autosuggest.component.css']
})
export class MaterialAutosuggestComponent implements OnDestroy {
  /** 1 = Dry, 2 = Liquid. */
  @Input() materialTypeId = 1;
  /** Material ids to hide from the results (already added). */
  @Input() excludeIds: number[] = [];
  @Input() placeholder = 'Search material…';
  @Output() select = new EventEmitter<MaterialSearchResult>();

  query = '';
  results: MaterialSearchResult[] = [];
  open = false;
  loading = false;

  // Legacy parity constants
  private readonly DEBOUNCE_MS = 100;        // Legacy: ~100ms debounce
  private readonly RESULT_CAP = 15;          // Legacy: 15-result client cap
  private readonly FLUID_BASED_DRY_MATERIAL = 2;  // MaterialTypeId for fluid-based dry materials

  private readonly term$ = new Subject<string>();
  private readonly sub: Subscription;

  constructor(private lookup: LookupService) {
    this.sub = this.term$.pipe(
      debounceTime(this.DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term || term.length === 0) {
          return of([] as MaterialSearchResult[]);
        }
        this.loading = true;
        return this.lookup.searchMaterials(term, this.materialTypeId).pipe(
          catchError(() => of([] as MaterialSearchResult[]))
        );
      })
    ).subscribe(res => {
      this.loading = false;
      const exclude = new Set(this.excludeIds);
      let filtered = res.filter(r => !exclude.has(r.id));
      if (this.materialTypeId === 1) {
        filtered = filtered.filter(r => r.materialTypeId !== this.FLUID_BASED_DRY_MATERIAL);
      }
      this.results = filtered.slice(0, this.RESULT_CAP);
      this.open = this.results.length > 0;
    });
  }

  onInput(): void {
    this.term$.next(this.query.trim());
  }

  choose(item: MaterialSearchResult): void {
    this.select.emit(item);
    this.query = '';
    this.results = [];
    this.open = false;
  }

  onBlur(): void {
    // Delay so a result click registers before the dropdown closes.
    setTimeout(() => (this.open = false), 150);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}

